import React, { useState } from 'react';
import {
  collection, query, where, getDocs, doc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { useOrgData } from '../../hooks/useOrgData';
import { logAudit } from '../../utils/auditLog';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { parseOfx } from '../../utils/ofxParser';
import { parseCsvTransactions } from '../../utils/csvImportParser';

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Normalize both parsers into one shape:
// { date, checkNum, payee, description, importId, lines: [{ fund, category, amount }] }
const normalizeOfx = (parsed) => parsed.map((r) => ({
  date: r.date,
  checkNum: r.checkNum || '',
  payee: r.name,
  description: r.memo,
  importId: r.fitId || '',
  lines: [{ fund: '', category: '', amount: r.amount }],
}));

const ImportTransactions = ({ orgId, onClose }) => {
  const { accounts, funds, transactionTypes } = useOrgData(orgId);
  const activeAccounts = accounts.filter(a => a.active !== false);
  const activeFunds    = funds.filter(f => f.active !== false);

  const [format,   setFormat]   = useState('ofx'); // 'ofx' | 'csv'
  const [step,     setStep]     = useState('select'); // 'select' | 'review'
  const [account,  setAccount]  = useState('');
  const [invert,   setInvert]   = useState(false);
  const [fileName, setFileName] = useState('');
  const [rawRows,  setRawRows]  = useState(null);
  const [rows,     setRows]     = useState([]);
  const [parsing,  setParsing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const dialogRef = useFocusTrap(onClose);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = String(evt.target.result || '');
      const parsed = format === 'csv' ? parseCsvTransactions(text) : normalizeOfx(parseOfx(text));
      setRawRows(parsed);
      if (parsed.length === 0) setError('No transactions found in this file.');
    };
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  };

  const handleParse = async () => {
    if (!account)              { setError('Please select an account.'); return; }
    if (!rawRows?.length)      { setError('Please choose a file with transactions to import.'); return; }

    setError('');
    setParsing(true);

    const snap = await getDocs(query(collection(db, 'orgs', orgId, 'transactions'), where('account', '==', account)));
    const existing = snap.docs.map(d => d.data());
    const existingImportIds = new Set(existing.filter(t => t.importId).map(t => t.importId));

    // Most recent category/fund used for each payee, to prefill new OFX rows
    const payeeMatch = {};
    if (format === 'ofx') {
      existing
        .slice()
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        .forEach(t => {
          if (t.split) return;
          const name = (t.payableTo || t.donor || '').toLowerCase();
          if (!name) return;
          payeeMatch[name] = { category: t.type || '', fund: t.classification || '' };
        });
    }

    const enriched = rawRows.map((r) => {
      const isDuplicate = !!r.importId && existingImportIds.has(r.importId);
      const lines = r.lines.map((l) => {
        const amount = format === 'ofx' && invert ? -l.amount : l.amount;
        if (format === 'ofx') {
          const match = payeeMatch[(r.payee || '').toLowerCase()];
          return { ...l, amount, category: match?.category || '', fund: match?.fund || '' };
        }
        return { ...l, amount };
      });
      return { ...r, lines, isDuplicate, include: !isDuplicate };
    });

    setRows(enriched);
    setStep('review');
    setParsing(false);
  };

  const updateLine = (rowIndex, lineIndex, field, value) => {
    setRows(rs => rs.map((r, i) => (i !== rowIndex ? r : {
      ...r,
      lines: r.lines.map((l, li) => (li !== lineIndex ? l : { ...l, [field]: value })),
    })));
  };

  const toggleInclude = (rowIndex, value) => {
    setRows(rs => rs.map((r, i) => (i === rowIndex ? { ...r, include: value } : r)));
  };

  const categoryOptionsFor = (amount) => {
    const kind = amount < 0 ? 'expense' : 'income';
    return transactionTypes.filter(t => t.kind === kind && t.active !== false);
  };

  const rowTotal = (row) => row.lines.reduce((s, l) => s + l.amount, 0);
  const included = rows.filter(r => r.include);
  const duplicateCount = rows.filter(r => r.isDuplicate).length;
  const incompleteCount = included.filter(r => r.lines.some(l => !l.category || !l.fund)).length;

  const handleCommit = async () => {
    if (included.length === 0)   { setError('Select at least one transaction to import.'); return; }
    if (incompleteCount > 0)     { setError('Every line needs a Fund and Category — check the highlighted rows.'); return; }

    setError('');
    setSaving(true);

    const u = auth.currentUser;
    const chunks = [];
    for (let i = 0; i < included.length; i += 400) chunks.push(included.slice(i, i + 400));

    try {
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((r) => {
          const ref = doc(collection(db, 'orgs', orgId, 'transactions'));
          const isStartingBalance = r.lines.length === 1 && r.lines[0].category === 'Starting Balance';
          const createdBy = {
            uid:         u?.uid || '',
            email:       u?.email || '',
            displayName: u?.displayName || u?.email || 'Unknown',
          };

          if (isStartingBalance) {
            batch.set(ref, {
              date:           r.date,
              stmtMonth:      r.date.substring(0, 7),
              account,
              type:           'Starting Balance',
              classification: r.lines[0].fund,
              description:    'Starting Balance',
              income:         Math.abs(r.lines[0].amount),
              expense:        0,
              year:           Number(r.date.substring(0, 4)),
              split:          false,
              cleared:        false,
              lines:          [],
              importId:       r.importId || '',
              createdAt:      serverTimestamp(),
              createdBy,
            });
            return;
          }

          const isSplit = r.lines.length > 1;
          const total = rowTotal(r);
          const isExpense = total < 0;

          batch.set(ref, {
            date:           r.date,
            stmtMonth:      r.date.substring(0, 7),
            account,
            cleared:        true,
            split:          isSplit,
            type:           isSplit ? 'Split' : r.lines[0].category,
            classification: isSplit ? '' : r.lines[0].fund,
            description:    isSplit ? '' : (r.description || ''),
            expense:        isExpense ? Math.abs(total) : 0,
            income:         isExpense ? 0 : Math.abs(total),
            year:           Number(r.date.substring(0, 4)),
            lines:          isSplit
              ? r.lines.map(l => ({ type: l.category, classification: l.fund, amount: Math.abs(l.amount), description: r.description || '' }))
              : [],
            checkNum:       r.checkNum || '',
            payableTo:      isExpense ? r.payee : '',
            donor:          isExpense ? '' : r.payee,
            importId:       r.importId || '',
            createdAt:      serverTimestamp(),
            createdBy,
          });
        });
        await batch.commit();
      }

      await logAudit(orgId, 'Transactions Imported', { account, count: included.length, format });
      onClose();
    } catch (err) {
      console.error('Import error:', err);
      setError(err?.message || 'Import failed. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-transactions-title"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 id="import-transactions-title" className="text-lg font-bold text-slate-800">
            Import Transactions
          </h3>
          <button onClick={onClose} aria-label="Close" title="Close" data-autofocus-skip className="text-gray-500 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{error}</p>
          )}

          {step === 'select' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Import format *</label>
                <select value={format} onChange={e => { setFormat(e.target.value); setRawRows(null); setFileName(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="ofx">Bank File (OFX/QFX)</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Account *</label>
                <select value={account} onChange={e => setAccount(e.target.value)} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="">Select account…</option>
                  {activeAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {format === 'csv' ? 'CSV file *' : 'OFX / QFX file *'}
                </label>
                <input type="file" accept={format === 'csv' ? '.csv' : '.ofx,.qfx'} onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:text-sm file:font-medium"
                />
                {fileName && rawRows && (
                  <p className="text-xs text-slate-500 mt-1">{fileName} — {rawRows.length} transaction{rawRows.length === 1 ? '' : 's'} found</p>
                )}
                {format === 'csv' && (
                  <p className="text-xs text-slate-500 mt-1">
                    Columns: date, checkNum, payee, description, fund, category, amount, group (amount is signed — negative = expense; rows sharing a "group" become one split transaction).
                  </p>
                )}
              </div>

              {format === 'ofx' && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" checked={invert} onChange={e => setInvert(e.target.checked)} className="rounded" />
                  <span>Invert amounts</span>
                  <span className="text-xs text-slate-500">(check this if expenses download as positive numbers)</span>
                </label>
              )}
            </>
          )}

          {step === 'review' && (
            <>
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>{account} — {rows.length} transaction{rows.length === 1 ? '' : 's'} parsed</span>
                {duplicateCount > 0 && (
                  <span className="text-amber-600">{duplicateCount} already imported, unchecked by default</span>
                )}
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide border-b border-gray-200">
                      <th className="px-3 py-2 text-left w-8"></th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Payee / Memo</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Fund</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => {
                      const total = rowTotal(r);
                      const isSplit = r.lines.length > 1;
                      return (
                        <tr key={i} className={r.isDuplicate ? 'opacity-50' : ''}>
                          <td className="px-3 py-2 align-top">
                            <input type="checkbox" checked={r.include} onChange={e => toggleInclude(i, e.target.checked)} className="rounded" />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600 align-top">{r.date}</td>
                          <td className="px-3 py-2 min-w-[10rem] align-top">
                            <div className="text-slate-700">{r.payee || '—'}</div>
                            {r.description && r.description !== r.payee && <div className="text-xs text-slate-500">{r.description}</div>}
                            {r.checkNum && <div className="text-xs text-slate-400">Check #{r.checkNum}</div>}
                            {r.isDuplicate && <div className="text-xs text-amber-600">Already imported</div>}
                          </td>
                          <td className={`px-3 py-2 text-right font-mono whitespace-nowrap align-top ${total < 0 ? 'text-red-500' : 'text-green-600'}`}>
                            {total < 0 ? `(${fmt(Math.abs(total))})` : fmt(total)}
                          </td>
                          {isSplit ? (
                            <td colSpan={2} className="px-3 py-2">
                              <div className="space-y-1.5">
                                {r.lines.map((l, li) => (
                                  <div key={li} className="flex items-center gap-2">
                                    <select value={l.category} onChange={e => updateLine(i, li, 'category', e.target.value)}
                                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 flex-1"
                                    >
                                      <option value="">Category…</option>
                                      {categoryOptionsFor(l.amount).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                    </select>
                                    <select value={l.fund} onChange={e => updateLine(i, li, 'fund', e.target.value)}
                                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 flex-1"
                                    >
                                      <option value="">Fund…</option>
                                      {activeFunds.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                                    </select>
                                    <span className="text-xs font-mono text-slate-500 whitespace-nowrap w-16 text-right">{fmt(Math.abs(l.amount))}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          ) : (
                            <>
                              <td className="px-3 py-2 align-top">
                                {r.lines[0].category === 'Starting Balance' ? (
                                  <span className="inline-block bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-lg">Starting Balance</span>
                                ) : (
                                  <select value={r.lines[0].category} onChange={e => updateLine(i, 0, 'category', e.target.value)}
                                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-[8rem]"
                                  >
                                    <option value="">Category…</option>
                                    {categoryOptionsFor(r.lines[0].amount).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                  </select>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <select value={r.lines[0].fund} onChange={e => updateLine(i, 0, 'fund', e.target.value)}
                                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-[8rem]"
                                >
                                  <option value="">Fund…</option>
                                  {activeFunds.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                                </select>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100">
          {step === 'select' ? (
            <>
              <button type="button" onClick={handleParse} disabled={parsing}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {parsing ? 'Reading…' : 'Continue'}
              </button>
              <button type="button" onClick={onClose}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setStep('select')}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button type="button" onClick={handleCommit} disabled={saving}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Importing…' : `Import ${included.length} Transaction${included.length === 1 ? '' : 's'}`}
              </button>
              <button type="button" onClick={onClose}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportTransactions;
